import React, {useState, useEffect, useContext, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import styles from './Dashboard.module.css';
import {PRISMAX_BACKEND_URL, PRISMAX_BACKEND_Control_URL} from '../../configs/config';
import {SwapMainAppContext} from "../../context/SwapMainAppContext";
import prismaPointSvg from '../../static_files/prisma-point.svg';
import prismaPointStackSvg from '../../static_files/prisma-point-stack.svg';
import prismaPointStackUpArrow from '../../static_files/prisma-point-stack-up-arrow.svg';
import timerDataHoursSvg from '../../static_files/timer-data-hours.svg';
import nextPageButton from "../../static_files/dashboardNextpage.svg";
import lastPageButton from "../../static_files/dashboardLastpage.svg";
import 'animate.css';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import dashboardVideo from "../../static_files/dashboard_video.MOV";
import validationVideo from "../../static_files/dashboard-validation-6517.mp4";
import monadArm1 from '../../static_files/monad-robot-arm.png';
import monadMoasto from "../../static_files/monad-moasto-dashboard.png";
import uploadRobotGoldBg from '../../static_files/dashboard-robot-background-gold-4x.png';
import uploadRobotBanner from '../../static_files/dashboard-robot-upload-banner-3x.png';
import {useIsMobile} from "../../utils/utils";
import useBackgroundColorChange from '../../utils/useBackgroundColorChange';

const VALIDATION_LAUNCH_TIME = Date.UTC(2026, 5, 23, 16, 0, 0);

const padCountdownPart = (value) => String(value).padStart(2, '0');

const getValidationCountdown = () => {
    let diff = VALIDATION_LAUNCH_TIME - Date.now();

    if (diff <= 0) {
        return {isLive: true, days: '00', hours: '00', minutes: '00', seconds: '00'};
    }

    const days = Math.floor(diff / 86400000);
    diff -= days * 86400000;
    const hours = Math.floor(diff / 3600000);
    diff -= hours * 3600000;
    const minutes = Math.floor(diff / 60000);
    diff -= minutes * 60000;
    const seconds = Math.floor(diff / 1000);

    return {
        isLive: false,
        days: padCountdownPart(days),
        hours: padCountdownPart(hours),
        minutes: padCountdownPart(minutes),
        seconds: padCountdownPart(seconds)
    };
};

const Dashboard = () => {
    const navigate = useNavigate();
    const [overallPoints, setOverallPoints] = useState(0);
    const [todayPoints, setTodayPoints] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalControlledHours, setTotalControlledHours] = useState(0);
    const [animatingTo, setAnimatingTo] = useState(null);
    const [isAnimating, setIsAnimating] = useState(false);
    const [animationDirection, setAnimationDirection] = useState(null);
    const [validationCountdown, setValidationCountdown] = useState(getValidationCountdown);
    const totalPages = 3;

    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);
    const isMobile = useIsMobile(930)
    const isSmallMobile = useIsMobile(420)
    const touchStartRef = useRef(null);
    const touchEndRef = useRef(null);

    const {
        userEmail,
        walletAddress,
        showFirstTimeModal,
        pointsRefreshTrigger,
        selectedChain,
        userId,
        gatewayToken
    } = useContext(SwapMainAppContext);

    useBackgroundColorChange(); // change the background color for the new design

    useEffect(() => {
        const updateCountdown = () => setValidationCountdown(getValidationCountdown());
        updateCountdown();

        const interval = setInterval(updateCountdown, 1000);
        return () => clearInterval(interval);
    }, []);

    // Function to handle page change with animation
    const changePage = (newPage) => {
        if (isAnimating) return;

        const targetPage = ((newPage % totalPages) + totalPages) % totalPages;
        if (targetPage === currentPage) return;

        const direction = newPage > currentPage ? 'forward' : 'backward';

        setIsAnimating(true);
        setAnimatingTo(targetPage);
        setAnimationDirection(direction);

        // Wait for exit animation to complete before changing page
        setTimeout(() => {
            setCurrentPage(targetPage);
            setIsAnimating(false);
            setAnimatingTo(null);
            setAnimationDirection(null);
        }, 500); // Half of the animation duration (1s total)
    };

    // Auto-rotate pagination every 15 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            changePage(currentPage + 1);
        }, 15000);

        return () => clearInterval(interval);
    }, [currentPage, isAnimating]);

    // Handle swipe gestures
    const handleTouchStart = (e) => {
        touchStartRef.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
        touchEndRef.current = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
        if (!touchStartRef.current || !touchEndRef.current) return;

        const distance = touchStartRef.current - touchEndRef.current;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe) {
            changePage(currentPage + 1);
        } else if (isRightSwipe) {
            changePage(currentPage - 1);
        }
    };

    useEffect(() => {
        if (walletAddress || userEmail) {
            fetchPointsData();
            fetchControlHistory();
        } else {
            setOverallPoints(0);
            setTodayPoints(0);
            setChartData([]);
            setTotalControlledHours(0);
        }
    }, [walletAddress, userEmail, showFirstTimeModal, pointsRefreshTrigger, userId, gatewayToken]);

    const fetchPointsData = async () => {
        try {
            const params = new URLSearchParams();
            if (userEmail) {
                params.append('email', userEmail);
            } else if (walletAddress) {
                params.append('wallet_address', walletAddress);
            }
            params.append('chain', selectedChain || 'solana');

            const queryParam = params.toString() ? `?${params.toString()}` : '';
            const transResponse = await fetch(`${PRISMAX_BACKEND_URL}/api/get-point-transactions${queryParam}`);

            if (transResponse.ok) {
                const transData = await transResponse.json();
                if (transData.success && transData.data) {
                    if (typeof transData.total_points !== 'undefined') {
                        const points = Math.floor(transData.total_points || 0);
                        setOverallPoints(points.toLocaleString());
                    }
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

                    const todayTransactions = transData.data.filter(trans => {
                        // Use user_local_date if available, otherwise convert UTC to local time
                        let transDate;
                        let transDateStr
                        if (trans.user_local_date) {
                            transDate = new Date(trans.user_local_date);
                            transDateStr = transDate.toISOString().split('T')[0];

                        } else if (trans.created_at_utc) {
                            // Convert UTC to local time
                            transDate = new Date(trans.created_at_utc);
                            // Get the date string in local timezone
                            const year = transDate.getFullYear();
                            const month = String(transDate.getMonth() + 1).padStart(2, '0');
                            const dayNum = String(transDate.getDate()).padStart(2, '0');
                            transDateStr = `${year}-${month}-${dayNum}`;
                        } else {

                            return false;
                        }


                        return transDateStr === todayStr && trans.points_change > 0;
                    });

                    const todayTotal = todayTransactions.reduce((sum, trans) => sum + trans.points_change, 0);
                    setTodayPoints(Math.floor(todayTotal));

                    prepareChartData(transData.data);
                }
            }
        } catch (error) {
            console.error('Error fetching points data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchControlHistory = async () => {
        try {
            if (!userId || !gatewayToken) {
                setTotalControlledHours(0);
                return;
            }

            const response = await fetch(`${PRISMAX_BACKEND_Control_URL}/user/control_history`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${gatewayToken}`
                },
                body: JSON.stringify({
                    userId: userId
                })
            });
            if (response.ok) {
                const data = await response.json();
                if (data && typeof data.total_controlled_hours !== 'undefined') {
                    setTotalControlledHours(data.total_controlled_hours);
                }
            }
        } catch (error) {
            console.error('Error fetching control history:', error);
            setTotalControlledHours(0);
        }
    };

    const prepareChartData = (transactions) => {
        const days = [];

        const cutNumber = isSmallMobile ? 6 : 12
        for (let i = cutNumber; i >= 0; i--) {
            const date = new Date();
            date.setDate(date.getDate() - i);
            date.setHours(0, 0, 0, 0);
            const localDateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            days.push(localDateStr);
        }


        const dailyPoints = days.map(day => {
            const dayTransactions = transactions.filter(trans => {
                // Use user_local_date if available, otherwise convert UTC to local time
                let transDate;
                let transDateStr
                if (trans.user_local_date) {
                    transDate = new Date(trans.user_local_date);
                    transDateStr = transDate.toISOString().split('T')[0];

                } else if (trans.created_at_utc) {
                    // Convert UTC to local time
                    transDate = new Date(trans.created_at_utc);
                    const year = transDate.getFullYear();
                    const month = String(transDate.getMonth() + 1).padStart(2, '0');
                    const dayNum = String(transDate.getDate()).padStart(2, '0');
                    transDateStr = `${year}-${month}-${dayNum}`;
                } else {
                    return false;
                }


                return transDateStr === day && trans.points_change > 0;
            });

            const total = dayTransactions.reduce((sum, trans) => sum + trans.points_change, 0);

            const [year, month, dayStr] = day.split('-');
            const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(dayStr));
            const monthShort = date.toLocaleDateString('en-US', {month: 'short'}).toLowerCase();
            const dayNum = String(date.getDate()).padStart(2, '0');

            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const todayLocalStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
            const isToday = day === todayLocalStr;

            return {
                date: day,
                points: total,
                label: `${dayNum}\n${monthShort}`,
                displayDate: `${dayNum} ${monthShort}`,
                isToday: isToday
            };
        });
        setChartData(dailyPoints);
    };

    const handleControlNow = () => {
        navigate('/live-control');
    };
    const handleBeginValidating = () => {
        navigate('/data/review');
    };
    const handleUploadData = () => {
        navigate('/data/upload')
    };
    const handleControlArm2 = () => {
        navigate('/tele-op/arm2');
    };

    // Function to get animation classes
    const getAnimationClass = (pageIndex) => {
        if (!isAnimating || animatingTo === null || !animationDirection) return '';

        if (pageIndex === currentPage) {
            return animationDirection === 'forward'
                ? 'animate__animated animate__slideOutLeft'
                : 'animate__animated animate__slideOutRight';
        }

        if (pageIndex === animatingTo) {
            return animationDirection === 'forward'
                ? 'animate__animated animate__slideInRight'
                : 'animate__animated animate__slideInLeft';
        }

        return '';
    };
    

    const shouldRenderPage = (pageIndex) => {
        return currentPage === pageIndex || (isAnimating && (animatingTo === pageIndex || currentPage === pageIndex));
    };

    const renderDots = (extraClass = '') => (
        <div className={[styles.paginationDots, extraClass].filter(Boolean).join(' ')}>
            {Array.from({length: totalPages}, (_, index) => (
                <div
                    key={index}
                    className={`${styles.dot} ${currentPage === index ? styles.activeDot : ''}`}
                    onClick={() => changePage(index)}
                />
            ))}
        </div>
    );

    const renderMonadControl = () => (
        <div className={`${styles.box} ${styles.reserveBox} ${styles.monadControlBox}`}
             onTouchStart={isMobile ? handleTouchStart : undefined}
             onTouchMove={isMobile ? handleTouchMove : undefined}
             onTouchEnd={isMobile ? handleTouchEnd : undefined}>
            <div className={`${styles.reserveContent} ${styles.reserveContent1}`}
                 style={{
                     flexDirection: isMobile ? 'column' : 'row',
                     alignItems: 'center',
                     gap: isMobile ? '6.5px' : '24px',
                     marginInline: isMobile ? '0' : '80px',
                 }}>
                {!isMobile && (<img src={monadArm1} alt='Monad robot arm'
                                    style={{width: isMobile ? '80%' : '26%', maxWidth: '280px', marginTop: '10px'}}/>)}

                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: isMobile?  '10px': '15px',
                    alignItems: 'center',
                    textAlign: 'center',
                    width: isMobile ? 'inherit' :'329px'
                }}>
                    {/* <h3 className={`${styles.monadTitle}`} style={{
                        fontSize: isMobile ? '24px' : '44px',
                        lineHeight: isMobile ? '30px' : '40px',
                        marginBottom: '6px',
                    }}> */}
                    <h3
                        className={`${styles.reserveTitleEditorial}`}
                        style={{
                            lineHeight: isMobile ? '30px' : '40px',
                            marginBottom: '6px',
                        }}
                    >
                        Teleop on Monad
                    </h3>
                    <h3 className={`${styles.monadTitle2}`} style={{
                    }}>
                        Earn 2X Points + $10k MON prize pool

                    </h3>
                    <div className={styles.dateTitle} style={{
                        fontSize: isMobile ? '15.4px' : '14px',
                        lineHeight: isMobile ? '19.3px' : '19.31px',
                        margin: 0
                    }}>
                        November 24th - December 25th
                    </div>
                    <button 
                        // className={styles.upgradeButton1}
                        // style={{backgroundImage: `url(${goldButton2})`}}
                        className={styles.mainButton}
                        onClick={handleControlArm2}
                    >
                        Control Now
                    </button>
                </div>

                {!isMobile && (<img src={monadMoasto} alt='Monad robot arm'
                                    style={{width: isMobile ? '80%' : '17%', maxWidth: '280px'}}/>)}
                {isMobile && (<div className={styles.monadArmImgContainer} style={{display: 'flex', gap: '14px'}}>
                    <img src={monadArm1} className={styles.monadArmImg} alt='Monad robot arm'
                         style={{width: '36vw', marginTop: '10px' }}/>
                    <img src={monadMoasto} className={styles.monadRobotImg} alt='Monad robot arm'
                         style={{width: '25vw'}}/>
                </div>)}

                {!isMobile && (
                     <div
                        className={`${styles.nextPage}`}
                        style={{height: '100%', display: 'flex', alignItems: 'center', right: '-80px'}}
                        onClick={() => changePage(1)}
                    >
                        <img src={nextPageButton} alt=''></img>
                    </div>
                )}
            </div>

            {isMobile && (
                <div
                    className={`${styles.nextPage} ${styles.nextPage2}`}
                    style={{height: '100%', display: 'flex', alignItems: 'center', right: '0px'}}
                    onClick={() => changePage(1)}
                >
                    <img src={nextPageButton} alt=''></img>
                </div>
            )}
       
            {renderDots(styles.paginationDots2)}
            
        </div>
    );

    const renderValidationCountdown = ({showClock = true} = {}) => (
        <div className={styles.countdown}>
            <div className={`${styles.cdEyebrow} ${validationCountdown.isLive ? styles.cdNow : ''}`}>
                <span className={styles.cdPulse}></span>
                {validationCountdown.isLive ? 'Validation is now live' : 'Goes live Tue, Jun 23 · 9:00 AM PDT'}
            </div>
            {!validationCountdown.isLive && showClock && (
                <div className={styles.cdClock}>
                    <div className={styles.cdUnit}><span className={styles.cdNum}>{validationCountdown.days}</span><span className={styles.cdLab}>days</span></div>
                    <span className={styles.cdColon}>:</span>
                    <div className={styles.cdUnit}><span className={styles.cdNum}>{validationCountdown.hours}</span><span className={styles.cdLab}>hrs</span></div>
                    <span className={styles.cdColon}>:</span>
                    <div className={styles.cdUnit}><span className={styles.cdNum}>{validationCountdown.minutes}</span><span className={styles.cdLab}>min</span></div>
                    <span className={styles.cdColon}>:</span>
                    <div className={styles.cdUnit}><span className={styles.cdNum}>{validationCountdown.seconds}</span><span className={styles.cdLab}>sec</span></div>
                </div>
            )}
        </div>
    );

    const renderUploadDataPage = ({prevTarget, nextTarget} = {}) => (
        isMobile ?
            <div className={styles.mobileReserveBoxHirePage}
                    onTouchStart={isMobile ? handleTouchStart : undefined}
                    onTouchMove={isMobile ? handleTouchMove : undefined}
                    onTouchEnd={isMobile ? handleTouchEnd : undefined}>
                <div className={styles.mobileRobotContainerHirePage}>
                    <div className={styles.uploadRobotVisual}>
                        <img src={uploadRobotGoldBg} alt='' className={styles.uploadRobotGoldBg}/>
                        <img src={uploadRobotBanner} alt='Robot arm' className={styles.uploadRobotImg}/>
                    </div>
                </div>
                <div className={styles.uploadTextScrim}></div>
                <div className={styles.mobileReserveContentHirePage}>
                    <div className={styles.mobileTextWrapperHirePage}>
                        <h3 className={styles.mobileReserveTitleControlPage}
                            style={{position: 'static'}}>
                            Your data.<br/>Real tasks. Real money.
                        </h3>
                        <p className={styles.mobileControlPageSubtitle} style={{position: 'static'}}>
                            Get paid for every quality-checked upload.
                        </p>
                        <button className={`${styles.mainButton} ${styles.mobileMainButtonControlPage} ${styles.mobileValidationButton}`}
                                style={{position: 'static'}}
                                onClick={handleUploadData}>
                            Upload Data
                        </button>
                    </div>
                </div>
                {renderDots(styles.paginationDots2)}
                {typeof prevTarget === 'number' && (
                    <div className={`${styles.nextPage} ${styles.nextPage2}`}
                        style={{height: '100%', display: 'flex', alignItems: 'center', left: '0px'}}
                        onClick={() => changePage(prevTarget)}
                    >
                        <img src={lastPageButton} alt=''></img>
                    </div>
                )}
                {typeof nextTarget === 'number' && (
                    <div className={`${styles.nextPage} ${styles.nextPage2}`}
                        style={{height: '100%', display: 'flex', alignItems: 'center', right: '0px'}}
                        onClick={() => changePage(nextTarget)}
                    >
                        <img src={nextPageButton} alt=''></img>
                    </div>
                )}
            </div> :
            <div className={`${styles.box} ${styles.reserveBox}`}
                    onTouchStart={isMobile ? handleTouchStart : undefined}
                    onTouchMove={isMobile ? handleTouchMove : undefined}
                    onTouchEnd={isMobile ? handleTouchEnd : undefined}>
                <div className={styles.reserveContent} style={{flexDirection: 'row', justifyContent: 'space-between'}}>
                    <div className={styles.reserveContentInner} style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: '24px',
                        alignItems: 'flex-start',
                        marginRight: '10px',
                        position: 'relative',
                        zIndex: 1
                    }}>
                        <h3 className={styles.reserveTitleEditorial}>Your data.<br/>Real tasks. Real money.</h3>
                        <p className={styles.controlPageSubtitle}>Get paid for every quality-checked upload.</p>
                        <button className={`${styles.mainButton} ${styles.uploadDataButton}`} onClick={handleUploadData}>
                            Upload Data
                        </button>
                    </div>
                    <div className={styles.uploadRobotVisual}>
                        <img src={uploadRobotGoldBg} alt='' className={styles.uploadRobotGoldBg}/>
                        <img src={uploadRobotBanner} alt='Robot arm' className={styles.uploadRobotImg}/>
                    </div>
                    {typeof prevTarget === 'number' && (
                        <div className={styles.nextPage}
                            style={{height: '100%', display: 'flex', alignItems: 'center', left: '-40px'}}
                            onClick={() => changePage(prevTarget)}
                        >
                            <img src={lastPageButton} alt=''></img>
                        </div>
                    )}
                    {typeof nextTarget === 'number' && (
                        <div className={styles.nextPage}
                            style={{height: '100%', display: 'flex', alignItems: 'center', right: '0px'}}
                            onClick={() => changePage(nextTarget)}
                        >
                            <img src={nextPageButton} alt=''></img>
                        </div>
                    )}
                </div>
                {renderDots()}
            </div>
    );

    const renderControlPage = ({
        prevTarget,
        nextTarget,
        title = <>Experience True <br/>Robotic Control</>,
        subtitle,
        countdownContent,
        isValidationBanner = false,
        buttonLabel = 'Control Now',
        onButtonClick = handleControlNow,
        videoSrc = dashboardVideo
    }) => (
        isMobile && isValidationBanner ?
            <div className={`${styles.box} ${styles.reserveBox} ${styles.mobileValidationBox}`}
                        onTouchStart={isMobile ? handleTouchStart : undefined}
                        onTouchMove={isMobile ? handleTouchMove : undefined}
                        onTouchEnd={isMobile ? handleTouchEnd : undefined}>
                <div className={styles.mobileValidationText}>
                    <h3 className={styles.mobileValidationTitle}>{title}</h3>
                    {subtitle && (
                        <p className={styles.mobileValidationSubtitle}>{subtitle}</p>
                    )}
                    {countdownContent}
                    <button className={`${styles.mainButton} ${styles.mobileValidationButton}`}
                            onClick={onButtonClick}>
                        {buttonLabel}
                    </button>
                </div>
                <div className={styles.mobileValidationMedia}>
                    <video className={styles.mobileValidationVideo} src={videoSrc}
                            autoPlay={true} muted={true} playsInline={true}
                            controls={false}
                            onContextMenu={(e) => e.preventDefault()}/>
                </div>
                {renderDots(styles.mobileValidationDots)}
                {typeof nextTarget === 'number' && (
                    <div className={`${styles.nextPage} ${styles.nextPage2}`}
                        style={{height: '100%', display: 'flex', alignItems: 'center', right: '0px'}}
                        onClick={() => changePage(nextTarget)}
                    >
                        <img src={nextPageButton} alt=''></img>
                    </div>
                )}
            </div> :
        isMobile ?
            <div className={`${styles.mobileContainer2} `}
                        onTouchStart={isMobile ? handleTouchStart : undefined}
                        onTouchMove={isMobile ? handleTouchMove : undefined}
                        onTouchEnd={isMobile ? handleTouchEnd : undefined}>
                <video className={styles.armVideo} src={videoSrc} width="auto" height="278"
                        autoPlay={true} muted={true} playsInline={true}
                        controls={false}
                        onContextMenu={(e) => e.preventDefault()}/>
                <h3 className={`${styles.mobileReserveTitleControlPage} ${subtitle ? styles.mobileReserveTitleWithSubtitle : ''}`}>
                    {title}
                </h3>
                {subtitle && (
                    <p className={styles.mobileControlPageSubtitle}>{subtitle}</p>
                )}
                <button className={`${styles.mainButton} ${styles.mobileMainButtonControlPage}`}
                        onClick={onButtonClick}>
                    {buttonLabel}
                </button>
                {typeof prevTarget === 'number' && (
                    <div className={`${styles.nextPage} ${styles.nextPage2}`}
                        style={{height: '100%', display: 'flex', alignItems: 'center', left: '0px'}}
                        onClick={() => changePage(prevTarget)}
                    >
                        <img src={lastPageButton} alt=''></img>
                    </div>
                )}
                {/* Pagination dots at the bottom */}
                {renderDots(styles.paginationDots2)}
                {typeof nextTarget === 'number' && (
                    <div className={`${styles.nextPage} ${styles.nextPage2}`}
                        style={{height: '100%', display: 'flex', alignItems: 'center', right: '0px'}}
                        onClick={() => changePage(nextTarget)}
                    >
                        <img src={nextPageButton} alt=''></img>
                    </div>
                )}
            </div> :
            <div className={`${styles.box} ${styles.reserveBox}`}
                    onTouchStart={isMobile ? handleTouchStart : undefined}
                    onTouchMove={isMobile ? handleTouchMove : undefined}
                    onTouchEnd={isMobile ? handleTouchEnd : undefined}>
                <div className={`${styles.reserveContent} ${subtitle ? styles.validationLayout : ''}`}
                        style={{flexDirection: 'row', justifyContent: 'space-between'}}>
                    <div className={`${styles.reserveContentInner} ${subtitle ? styles.validationContent : ''}`} style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: '24px',
                        alignItems: 'flex-start',
                        marginRight: '10px'
                    }}>
                        <h3 className={styles.reserveTitleEditorial}>{title}</h3>
                        {subtitle && (
                            <p className={styles.controlPageSubtitle}>{subtitle}</p>
                        )}
                        {countdownContent}
                        <button className={styles.mainButton}
                                onClick={onButtonClick}>
                            {buttonLabel}
                        </button>
                    </div>
                    <video className={`${styles.armVideo} ${subtitle ? styles.validationVideo : ''}`} src={videoSrc} width="auto" height="334"
                        autoPlay={true} muted={true} playsInline={true}
                        controls={false}
                        onContextMenu={(e) => e.preventDefault()}/>
                    {typeof prevTarget === 'number' && (
                        <div className={styles.nextPage}
                            style={{height: '100%', display: 'flex', alignItems: 'center', left: '-40px'}}
                            onClick={() => changePage(prevTarget)}
                        >
                            <img src={lastPageButton} alt=''></img>
                        </div>
                    )}
                    {typeof nextTarget === 'number' && (
                        <div className={styles.nextPage}
                            style={{height: '100%', display: 'flex', alignItems: 'center', right: '0px'}}
                            onClick={() => changePage(nextTarget)}
                        >
                            <img src={nextPageButton} alt=''></img>
                        </div>
                    )}
                </div>
                {/* Pagination dots at the bottom */}
                {renderDots()}
            </div>
    );

    return (
        <div className={styles.container}>
            {/* Top row */}
            <div className={`${styles.row} ${styles.dashboardCarouselRow}`} style={{position: 'relative', overflow: 'hidden'}}>
                {/* Monad promo page hidden for now. Uncomment to restore later.
                {shouldRenderPage(0) && (
                    <div className={getAnimationClass(0)} style={{
                        position: isAnimating ? 'absolute' : 'relative',
                        width: '100%',
                        top: 0,
                        left: 0
                    }}>
                        {renderMonadControl()}
                    </div>
                )}
                */}

                {shouldRenderPage(0) && (
                    <div className={getAnimationClass(0)} style={{
                        position: isAnimating ? 'absolute' : 'relative',
                        width: '100%',
                        top: 0,
                        left: 0
                    }}>
                        {renderControlPage({
                            nextTarget: 1,
                            title: <>The standard<br/>starts with you.</>,
                            subtitle: 'Validate data. Earn points.',
                            countdownContent: renderValidationCountdown({showClock: !isMobile}),
                            isValidationBanner: true,
                            buttonLabel: 'Begin Validating',
                            onButtonClick: handleBeginValidating,
                            videoSrc: validationVideo
                        })}
                    </div>
                )}

                {shouldRenderPage(1) && (
                    <div className={getAnimationClass(1)} style={{
                        position: isAnimating ? 'absolute' : 'relative',
                        width: '100%',
                        top: 0,
                        left: 0
                    }}>
                        {renderUploadDataPage({prevTarget: 0, nextTarget: 2})}
                    </div>
                )}

                {shouldRenderPage(2) && (
                    <div className={getAnimationClass(2)} style={{
                        position: isAnimating ? 'absolute' : 'relative',
                        width: '100%',
                        top: 0,
                        left: 0
                    }}>
                        {renderControlPage({prevTarget: 1})}
                    </div>
                )}
            </div>

            {/* Middle row - three boxes for points data */}
            <div className={styles.row} style={{minHeight: '0'}}>
                <div className={styles.box} style={{padding: '16px'}}>
                    <div className={styles.dataContent}>
                        <span className={styles.boxTitle}>All-Time Prisma Points</span>
                        <div className={styles.pointsDisplay}>
                             <img
                                src={prismaPointSvg}
                                alt='All-Time Prisma Points'
                                className={styles.pointsIcon}
                            />
                            <span className={styles.pointsValue}>{loading ? '...' : overallPoints}</span>
                        </div>
                    </div>
                </div>

                <div className={styles.box} style={{padding: '16px'}}>
                    <div className={styles.dataContent}>
                        <span className={styles.boxTitle}>Daily Prisma Points</span>
                        <div className={styles.pointsDisplay}>
                            <img
                                src={prismaPointStackSvg} 
                                alt='Daily Prisma Points'
                                className={styles.pointsIcon}
                            />
                            <img
                                src={prismaPointStackUpArrow}
                                className={styles.dailyPrismaPointsUpArrowIcon}
                            />
                            <span className={styles.pointsValue}>{loading ? '...' : todayPoints}</span>
                           
                        </div>
                        
                    </div>
                </div>

                <div className={styles.box} style={{padding: '16px'}}>
                    <div className={styles.dataContent}>
                        <span className={styles.boxTitle}>Data Hours Contributed</span>
                        <div className={styles.pointsDisplay}>
                            <img
                                src={timerDataHoursSvg}
                                alt='Data Hours Contributed'
                                className={styles.pointsIcon} 
                            />
                            <span className={styles.pointsValue}>{loading ? '...' : totalControlledHours}</span>
                        </div>
                    </div>
                </div>

            </div>
        
            {/* Bottom row - one large box for earnings chart */}
            <div className={styles.fullWidthBox}>
                <h3 className={styles.chartTitleEarnings}>Earnings</h3>
                <div className={styles.lineChartContainer}>
                    <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: isSmallMobile ? 30 : 20 }}>
                            <XAxis
                                dataKey={isSmallMobile ? 'label' : 'displayDate'}
                                stroke='transparent'
                                tick={{ fill: '#666', fontSize: 12 }}
                                tickLine={false}
                                axisLine={false}
                                interval='preserveStart'
                                angle={0}
                                textAnchor='middle'
                                height={isSmallMobile ? 40 : undefined}
                            />
                            <YAxis
                                hide={true}
                            />
                            <Tooltip
                                cursor={{ stroke: 'rgba(217, 217, 217, 0.3)', strokeWidth: 1 }}
                                contentStyle={{
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    color: 'white',
                                    fontSize: '14px',
                                    padding: '0'
                                }}
                                formatter={(value) => [`${value} pts`]}
                                labelFormatter={() => ''}
                                itemStyle={{ color: 'white' }}
                            />
                            <Line
                                type='linear'
                                dataKey='points'
                                stroke='rgba(217, 217, 217, 0.1)'
                                strokeWidth={1}
                                dot={false}
                                activeDot={{ r: 4.5, fill: '#B9986A', stroke: 'none' }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
